import React, { useEffect, useState, useCallback } from "react";
import { useDropzone, FileRejection } from "react-dropzone";
import { callApi, HttpMethod } from "src/services/apiService";
import Loading from "src/components/Loading";
import SaveInProgress from "src/components/SaveInProgress";
import FileUploadContainer from "src/components/FileUploadContainer";
import {
  IDisplayRecord,
  IDisplayIdentityRecord,
  ICallReason,
} from "src/interfaces/LogoCall/IDisplayIdentityRecord";
import BCIDDocumentUpload from "src/components/LogoCall/BCIDDocumentUpload";
import ConfirmationModal from "src/components/ConfirmationModal";

const resizeImage = (
  file: File,
  width: number,
  height: number,
): Promise<File> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      if (!e.target?.result) {
        return reject(new Error("Failed to read file."));
      }
      const img = new Image();
      img.src = e.target.result as string;
      img.onload = () => {
        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext("2d");
        if (!ctx) {
          return reject(new Error("Could not get canvas context"));
        }
        ctx.drawImage(img, 0, 0, width, height);
        canvas.toBlob(
          (blob) => {
            if (blob) {
              const resizedFile = new File([blob], file.name, {
                type: file.type,
              });
              resolve(resizedFile);
            } else {
              reject(new Error("Canvas is empty"));
            }
          },
          file.type,
          1,
        );
      };
      img.onerror = () => reject(new Error("Failed to load image"));
    };
    reader.onerror = () => reject(new Error("Failed to read file"));
    reader.readAsDataURL(file);
  });
};

interface DisplayRecordProps {
  isOpen: boolean;
  onClose: () => void;
  displayRecordId: string | null;
  canWithdraw?: boolean;
  canRevert?: boolean;
  canEdit?: boolean;
}

const DisplayRecordModal: React.FC<DisplayRecordProps> = ({
  isOpen,
  onClose,
  displayRecordId,
  canWithdraw = false,
  canRevert = false,
  canEdit = true,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [warningMessage, setWarningMessage] = useState("");
  const [_data, setData] = useState<IDisplayRecord | null>();
  const [dir, setDir] = useState<IDisplayIdentityRecord>();
  const [saveInProgress, setSaveInProgress] = useState(false);
  const [callReasons, setCallReasons] = useState<ICallReason[]>([]);
  const [newCallReasonText, setNewCallReasonText] = useState("");
  const [editingCallReasonId, setEditingCallReasonId] = useState<number | null>(
    null,
  );
  const [editingCallReasonText, setEditingCallReasonText] = useState("");
  const [isAuthorized, setIsAuthorized] = useState(true);
  const [callingName, setCallingName] = useState("");
  const [_logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [fileErrorMessage, setFileErrorMessage] = useState("");
  const [activeTab, setActiveTab] = useState("identity");
  const [displayNameMatchesBrand, setDisplayNameMatchesBrand] = useState("");
  const [callReasonsAlignWithActivity, setCallReasonsAlignWithActivity] =
    useState("");
  const [shaftRelatedCalls, setShaftRelatedCalls] = useState("");
  const [shaftComplianceJustification, setShaftComplianceJustification] =
    useState("");
  const [ownsAllBranding, setOwnsAllBranding] = useState("");
  const [numberOwnershipProof, setNumberOwnershipProof] = useState("");
  const [voiceServiceProvider, setVoiceServiceProvider] = useState("");
  const [numberSourceDescription, setNumberSourceDescription] = useState("");
  const [numberOwnershipDuration, setNumberOwnershipDuration] = useState("");
  const [isFormDirty, setIsFormDirty] = useState(false);
  const [unsavedTabTarget, setUnsavedTabTarget] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<
    Record<string, string>
  >({});

  const fetchDisplayRecordData = useCallback(async () => {
    if (!displayRecordId) {
      // Abhi: Reset the form as the user might have clicked edit and now come to Adding a new Display Record
      setData(null);
      setDir(undefined);
      setCallingName("");
      setLogoPreview("");
      setCallReasons([]);
      setDisplayNameMatchesBrand("");
      setCallReasonsAlignWithActivity("");
      setShaftRelatedCalls("");
      setShaftComplianceJustification("");
      setOwnsAllBranding("");
      setNumberOwnershipProof("");
      setVoiceServiceProvider("");
      setNumberSourceDescription("");
      setNumberOwnershipDuration("");
      setValidationErrors({});
      setIsFormDirty(false);
      return;
    }
    setIsLoading(true);
    try {
      const apiUrl = `bcid/dir/${displayRecordId}`;
      const response = await callApi(apiUrl, HttpMethod.GET);
      setData(response);
      setDir(response.DisplayIdentityRecord);
      setCallingName(response.DisplayIdentityRecord.rcdName);
      setLogoPreview(response.DisplayIdentityRecord.logoUrl);
      setCallReasons(response.CallReasons || []);
      setDisplayNameMatchesBrand(
        response.DisplayIdentityRecord.displayNameMatchesBrand || "",
      );
      setCallReasonsAlignWithActivity(
        response.DisplayIdentityRecord.callReasonsAlignWithActivity || "",
      );
      setShaftRelatedCalls(
        response.DisplayIdentityRecord.shaftRelatedCalls || "",
      );
      setShaftComplianceJustification(
        response.DisplayIdentityRecord.shaftComplianceJustification || "",
      );
      setOwnsAllBranding(response.DisplayIdentityRecord.ownsAllBranding || "");
      setNumberOwnershipProof(
        response.DisplayIdentityRecord.numberOwnershipProof || "",
      );
      setVoiceServiceProvider(
        response.DisplayIdentityRecord.voiceServiceProvider || "",
      );
      setNumberSourceDescription(
        response.DisplayIdentityRecord.numberSourceDescription || "",
      );
      setNumberOwnershipDuration(
        response.DisplayIdentityRecord.numberOwnershipDuration || "",
      );
    } catch (error) {
      setWarningMessage("Failed to fetch data.");
      console.error("Failed to fetch data:", error);
    } finally {
      setIsLoading(false);
      setIsFormDirty(false);
    }
  }, [displayRecordId]);

  useEffect(() => {
    fetchDisplayRecordData();
    setActiveTab("identity");
  }, [displayRecordId, fetchDisplayRecordData]);

  useEffect(() => {
    if (isOpen) {
      setActiveTab("identity");
    }
  }, [isOpen]);

  const requestDirTabChange = (nextTab: string) => {
    if (nextTab === activeTab) return;
    if (isLoading) {
      setActiveTab(nextTab);
      return;
    }
    if (isFormDirty) {
      setUnsavedTabTarget(nextTab);
      return;
    }
    setActiveTab(nextTab);
  };

  const fileToBase64 = async (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        if (typeof reader.result === "string") {
          resolve(reader.result);
        } else {
          reject(new Error("Conversion failed"));
        }
      };
      reader.onerror = (error) => reject(error);
      reader.readAsDataURL(file);
    });
  };

  const onDrop = async (acceptedFiles: File[]) => {
    if (acceptedFiles && acceptedFiles[0]) {
      const file = acceptedFiles[0];
      try {
        const resizedFile = await resizeImage(file, 256, 256);
        setLogoFile(resizedFile);
        const base64 = await fileToBase64(resizedFile);
        setLogoPreview(base64);
        setFileErrorMessage("");
        setIsFormDirty(true);
      } catch (error) {
        console.error("Error resizing image", error);
      }
    }
  };

  const onDropRejected = (fileRejections: FileRejection[]) => {
    if (fileRejections.length > 0) {
      setFileErrorMessage("Please upload a valid image file (jpg, png, etc.)");
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    onDropRejected,
    accept: { "image/*": [] },
  });

  const handleAddCallReason = () => {
    if (!newCallReasonText.trim() || callReasons.length >= 10) return;
    setCallReasons([...callReasons, { CallReason: newCallReasonText }]);
    setNewCallReasonText("");
    setIsFormDirty(true);
    if (validationErrors.callReasons) {
      setValidationErrors((prev) => {
        const { callReasons: _, ...rest } = prev;
        return rest;
      });
    }
  };

  const handleEditCallReason = (index: number) => {
    setEditingCallReasonId(index);
    setEditingCallReasonText(callReasons[index].CallReason);
  };

  const handleSaveCallReason = (index: number) => {
    if (!editingCallReasonText.trim()) return;
    const updatedCallReasons = callReasons.map((reason, i) =>
      i === index ? { CallReason: editingCallReasonText } : reason,
    );
    setCallReasons(updatedCallReasons);
    setEditingCallReasonId(null);
    setEditingCallReasonText("");
    setIsFormDirty(true);
  };

  const handleDeleteCallReason = (index: number) => {
    const updatedCallReasons = callReasons.filter((_, i) => i !== index);
    setCallReasons(updatedCallReasons);
    setIsFormDirty(true);
  };

  // Maps field names to their tab for error highlighting
  const FIELD_TAB_MAP: Record<string, { tab: string; label: string }> = {
    callReasons: { tab: "identity", label: "Call Reasons" },
    displayNameMatchesBrand: { tab: "rcd", label: "Display name match" },
    callReasonsAlignWithActivity: { tab: "rcd", label: "Call reasons alignment" },
    shaftRelatedCalls: { tab: "shaft", label: "Illegal activity calls" },
    voiceServiceProvider: { tab: "ip", label: "Voice Service Provider" },
    numberSourceDescription: { tab: "ip", label: "Number source description" },
  };

  const getTabErrors = () => {
    const tabs = new Set<string>();
    for (const field of Object.keys(validationErrors)) {
      const mapping = FIELD_TAB_MAP[field];
      if (mapping) tabs.add(mapping.tab);
    }
    return tabs;
  };

  const validateForSubmission = (): boolean => {
    const errors: Record<string, string> = {};
    if (callReasons.length === 0) {
      errors.callReasons = "At least one call reason is required";
    }
    if (!displayNameMatchesBrand.trim()) {
      errors.displayNameMatchesBrand = "This field is required";
    }
    if (!callReasonsAlignWithActivity.trim()) {
      errors.callReasonsAlignWithActivity = "This field is required";
    }
    if (!shaftRelatedCalls.trim()) {
      errors.shaftRelatedCalls = "This field is required";
    }
    if (!voiceServiceProvider.trim()) {
      errors.voiceServiceProvider = "This field is required";
    }
    if (!numberSourceDescription.trim()) {
      errors.numberSourceDescription = "This field is required";
    }
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSave = async (isDraft: boolean): Promise<boolean> => {
    if (!isDraft) {
      const isValid = validateForSubmission();
      if (!isValid) {
        setWarningMessage(
          "Some required fields are missing. Check each tab for fields marked in red.",
        );
        return false;
      }
    }
    const displayRecord: IDisplayIdentityRecord = {
      displayIdentityRecordId: dir?.displayIdentityRecordId || null,
      enterpriseId: dir?.enterpriseId || null,
      rcdName: callingName,
      logoUrl:
        logoPreview && logoPreview.startsWith("http")
          ? ""
          : (logoPreview ?? ""),
      displayNameMatchesBrand: displayNameMatchesBrand || null,
      callReasonsAlignWithActivity: callReasonsAlignWithActivity || null,
      shaftRelatedCalls: shaftRelatedCalls || null,
      shaftComplianceJustification: shaftComplianceJustification || null,
      ownsAllBranding: ownsAllBranding || null,
      numberOwnershipProof: numberOwnershipProof || null,
      voiceServiceProvider: voiceServiceProvider || null,
      numberSourceDescription: numberSourceDescription || null,
      numberOwnershipDuration: numberOwnershipDuration || null,
    };
    const payload: IDisplayRecord = {
      isDraft,
      DisplayIdentityRecord: displayRecord,
      CallReasons: callReasons,
    };

    try {
      setSaveInProgress(true);
      setWarningMessage("");
      const response = await callApi("bcid/dir/save", HttpMethod.POST, payload);
      console.log("Save successful", response);
      // Update the dir state with the saved ID so subsequent saves update the same record
      if (response?.displayIdentityRecordId) {
        setDir((prev) => ({
          ...(prev ?? displayRecord),
          displayIdentityRecordId: response.displayIdentityRecordId,
        }));
      }
      setIsFormDirty(false);
      setValidationErrors({});
      if (!isDraft) {
        onClose();
      }
      return true;
    } catch (error) {
      setWarningMessage(
        "There was an issue saving the record. Please try again.",
      );
      console.error("Error saving", error);
      return false;
    } finally {
      setSaveInProgress(false);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="modal-backdrop show" />
      <div className="modal show d-block" tabIndex={-1}>
        <div className="modal-dialog modal-lg">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">
                {displayRecordId ? "Edit" : "Add"} Display Record
              </h5>
              <button
                type="button"
                className="btn-close"
                onClick={onClose}
                aria-label="Close modal"
              ></button>
            </div>
            <div className="modal-body">
              {isLoading && <Loading />}
              {warningMessage && (
                <div className="alert alert-warning">{warningMessage}</div>
              )}
              {/* Tab Navigation */}
              <ul className="nav nav-tabs mb-3" style={{ gap: "4px" }}>
                {[
                  { key: "identity", label: "Display Identity" },
                  { key: "rcd", label: "RCD Validation" },
                  { key: "shaft", label: "Content Compliance" },
                  { key: "ip", label: "IP Validation" },
                ].map((tab, index) => {
                  const tabHasErrors = getTabErrors().has(tab.key);
                  return (
                  <li className="nav-item" key={tab.key}>
                    <button
                      type="button"
                      className={`nav-link ${activeTab === tab.key ? "active" : ""}`}
                      onClick={() => requestDirTabChange(tab.key)}
                    >
                      <span
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          width: "20px",
                          height: "20px",
                          borderRadius: "50%",
                          backgroundColor: tabHasErrors
                            ? "#dc3545"
                            : activeTab === tab.key
                              ? "#0d6efd"
                              : "#6c757d",
                          color: "#fff",
                          fontSize: "0.7rem",
                          fontWeight: 600,
                          marginRight: "6px",
                        }}
                      >
                        {index + 1}
                      </span>
                      <span style={tabHasErrors ? { color: "#dc3545", fontWeight: 600 } : undefined}>
                        {tab.label}
                      </span>
                    </button>
                  </li>
                  );
                })}
              </ul>

              <div
                className="d-flex gap-4"
                onChange={() => setIsFormDirty(true)}
              >
                <div className="flex-grow-1">
                  {/* Tab 1: Display Identity */}
                  <div
                    style={{
                      display: activeTab === "identity" ? "block" : "none",
                    }}
                  >
                    <div className="mb-3">
                      <label className="form-label">
                        <b>Calling Name</b>
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        value={callingName}
                        onChange={(e) => setCallingName(e.target.value)}
                      />
                    </div>

                    <div className="mb-3">
                      <div className="row">
                        <div className="col-12">
                          <label className="form-label">
                            <b>Call Logo</b>
                          </label>
                        </div>
                      </div>
                      <div className="row align-items-start">
                        <div className="col">
                          <FileUploadContainer
                            getRootProps={getRootProps}
                            getInputProps={getInputProps}
                            isDragActive={isDragActive}
                            title="Upload Logo"
                            iconClassName="bi-upload"
                          />
                          {fileErrorMessage && (
                            <div className="text-danger mt-2">
                              {fileErrorMessage}
                            </div>
                          )}
                        </div>
                        <div className="col-auto">
                          <div
                            className="border"
                            style={{ width: "100px", height: "100px" }}
                          >
                            {logoPreview && (
                              <img
                                src={logoPreview}
                                alt="Logo Preview"
                                style={{
                                  width: "100%",
                                  height: "100%",
                                  objectFit: "contain",
                                }}
                              />
                            )}
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="mb-3">
                      <label className="form-label">
                        <b>Call Reason*</b>
                        <small className="text-muted ps-2">(max 10)</small>
                      </label>
                      {validationErrors.callReasons && (
                        <div className="text-danger small mb-2">
                          {validationErrors.callReasons}
                        </div>
                      )}
                      <div className="d-flex gap-2">
                        <input
                          type="text"
                          className="form-control"
                          placeholder="Call Reasons e.g., 'Delivery Confirmation'"
                          value={newCallReasonText}
                          onChange={(e) => setNewCallReasonText(e.target.value)}
                        />
                        <button
                          className="btn btn-secondary"
                          onClick={handleAddCallReason}
                          disabled={callReasons.length >= 10}
                        >
                          Add
                        </button>
                      </div>
                      <table className="table table-dark mt-3">
                        <thead>
                          <tr>
                            <th>Call Reason</th>
                          </tr>
                        </thead>
                        <tbody>
                          {callReasons.map((cr, index) => (
                            <tr key={index}>
                              <td>
                                <div className="d-flex justify-content-between align-items-center">
                                  {editingCallReasonId === index ? (
                                    <input
                                      type="text"
                                      value={editingCallReasonText}
                                      onChange={(e) =>
                                        setEditingCallReasonText(e.target.value)
                                      }
                                      className="form-control form-control-sm"
                                    />
                                  ) : (
                                    <span>{cr.CallReason}</span>
                                  )}
                                  <div className="d-flex gap-2 align-items-end">
                                    {editingCallReasonId === index ? (
                                      <>
                                        <button
                                          className="btn btn-sm btn-primary"
                                          onClick={() =>
                                            handleSaveCallReason(index)
                                          }
                                        >
                                          Save
                                        </button>
                                        <button
                                          className="btn btn-sm btn-secondary"
                                          onClick={() => {
                                            setEditingCallReasonId(null);
                                            setEditingCallReasonText("");
                                          }}
                                        >
                                          Cancel
                                        </button>
                                      </>
                                    ) : (
                                      <>
                                        <button
                                          className="btn btn-sm btn-link text-primary"
                                          onClick={(e) => {
                                            e.preventDefault();
                                            handleEditCallReason(index);
                                          }}
                                        >
                                          Edit
                                        </button>
                                        <button
                                          className="btn btn-sm btn-link text-primary"
                                          onClick={(e) => {
                                            e.preventDefault();
                                            handleDeleteCallReason(index);
                                          }}
                                        >
                                          Delete
                                        </button>
                                      </>
                                    )}
                                  </div>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* Tab 2: RCD Validation */}
                  <div
                    style={{ display: activeTab === "rcd" ? "block" : "none" }}
                  >
                    <div className="mb-3">
                      <label className="form-label">
                        Does display name match legal/brand/DBA?*
                      </label>
                      <input
                        type="text"
                        className={`form-control ${validationErrors.displayNameMatchesBrand ? "is-invalid" : ""}`}
                        value={displayNameMatchesBrand}
                        onChange={(e) => {
                          setDisplayNameMatchesBrand(e.target.value);
                          if (validationErrors.displayNameMatchesBrand) {
                            setValidationErrors((prev) => {
                              const { displayNameMatchesBrand: _, ...rest } = prev;
                              return rest;
                            });
                          }
                        }}
                      />
                      {validationErrors.displayNameMatchesBrand && (
                        <div className="invalid-feedback">
                          {validationErrors.displayNameMatchesBrand}
                        </div>
                      )}
                    </div>
                    <div className="mb-3">
                      <BCIDDocumentUpload
                        entityId={dir?.displayIdentityRecordId || null}
                        documentType="LogoOwnershipProof"
                        label="Proof of Logo Ownership or Rights (trademark/license)"
                      />
                    </div>
                    <div className="mb-3">
                      <label className="form-label">
                        Do call reasons align with business activity?*
                      </label>
                      <input
                        type="text"
                        className={`form-control ${validationErrors.callReasonsAlignWithActivity ? "is-invalid" : ""}`}
                        value={callReasonsAlignWithActivity}
                        onChange={(e) => {
                          setCallReasonsAlignWithActivity(e.target.value);
                          if (validationErrors.callReasonsAlignWithActivity) {
                            setValidationErrors((prev) => {
                              const { callReasonsAlignWithActivity: _, ...rest } = prev;
                              return rest;
                            });
                          }
                        }}
                      />
                      {validationErrors.callReasonsAlignWithActivity && (
                        <div className="invalid-feedback">
                          {validationErrors.callReasonsAlignWithActivity}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Tab 3: Content Compliance (SHAFT) */}
                  <div
                    style={{
                      display: activeTab === "shaft" ? "block" : "none",
                    }}
                  >
                    <div className="mb-3">
                      <label className="form-label">
                        Do any calls relate to illegal activity?*
                      </label>
                      <textarea
                        className={`form-control ${validationErrors.shaftRelatedCalls ? "is-invalid" : ""}`}
                        rows={2}
                        value={shaftRelatedCalls}
                        onChange={(e) => {
                          setShaftRelatedCalls(e.target.value);
                          if (validationErrors.shaftRelatedCalls) {
                            setValidationErrors((prev) => {
                              const { shaftRelatedCalls: _, ...rest } = prev;
                              return rest;
                            });
                          }
                        }}
                      />
                      {validationErrors.shaftRelatedCalls && (
                        <div className="invalid-feedback">
                          {validationErrors.shaftRelatedCalls}
                        </div>
                      )}
                    </div>
                    <div className="mb-3">
                      <label className="form-label">
                        If yes, explain and provide compliance justification
                      </label>
                      <textarea
                        className="form-control"
                        rows={2}
                        value={shaftComplianceJustification}
                        onChange={(e) =>
                          setShaftComplianceJustification(e.target.value)
                        }
                      />
                    </div>
                  </div>

                  {/* Tab 4: Intellectual Property Validation */}
                  <div
                    style={{ display: activeTab === "ip" ? "block" : "none" }}
                  >
                    <div className="mb-3">
                      <label className="form-label">
                        Do you own all branding used?
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        value={ownsAllBranding}
                        onChange={(e) => setOwnsAllBranding(e.target.value)}
                      />
                    </div>
                    <div className="mb-3">
                      <BCIDDocumentUpload
                        entityId={dir?.displayIdentityRecordId || null}
                        documentType="TrademarkDocumentation"
                        label="Provide Trademark or Licensing Documentation"
                      />
                    </div>
                    <div className="mb-3">
                      <label className="form-label">
                        Proof of ownership or control of numbers
                      </label>
                      <textarea
                        className="form-control"
                        rows={2}
                        value={numberOwnershipProof}
                        onChange={(e) =>
                          setNumberOwnershipProof(e.target.value)
                        }
                      />
                    </div>
                    <div className="mb-3">
                      <label className="form-label">
                        Who is your Voice Service Provider?*
                      </label>
                      <input
                        type="text"
                        className={`form-control ${validationErrors.voiceServiceProvider ? "is-invalid" : ""}`}
                        value={voiceServiceProvider}
                        onChange={(e) => {
                          setVoiceServiceProvider(e.target.value);
                          if (validationErrors.voiceServiceProvider) {
                            setValidationErrors((prev) => {
                              const { voiceServiceProvider: _, ...rest } = prev;
                              return rest;
                            });
                          }
                        }}
                      />
                      {validationErrors.voiceServiceProvider && (
                        <div className="invalid-feedback">
                          {validationErrors.voiceServiceProvider}
                        </div>
                      )}
                    </div>
                    <div className="mb-3">
                      <label className="form-label">
                        Does your Voice Service Provider provide you with the
                        numbers? If not, how?*
                      </label>
                      <textarea
                        className={`form-control ${validationErrors.numberSourceDescription ? "is-invalid" : ""}`}
                        rows={2}
                        value={numberSourceDescription}
                        onChange={(e) => {
                          setNumberSourceDescription(e.target.value);
                          if (validationErrors.numberSourceDescription) {
                            setValidationErrors((prev) => {
                              const { numberSourceDescription: _, ...rest } = prev;
                              return rest;
                            });
                          }
                        }}
                      />
                      {validationErrors.numberSourceDescription && (
                        <div className="invalid-feedback">
                          {validationErrors.numberSourceDescription}
                        </div>
                      )}
                    </div>
                    <div className="mb-3">
                      <label className="form-label">
                        How long have you owned your calling numbers?
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        value={numberOwnershipDuration}
                        onChange={(e) =>
                          setNumberOwnershipDuration(e.target.value)
                        }
                      />
                    </div>
                  </div>

                  {activeTab !== "ip" ? (
                    <div className="mt-3 d-flex gap-2">
                      <button
                        type="button"
                        className="btn btn-outline-primary"
                        onClick={async () => {
                          const success = await handleSave(true);
                          if (success) {
                            const tabs = ["identity", "rcd", "shaft", "ip"];
                            const currentIndex = tabs.indexOf(activeTab);
                            if (currentIndex < tabs.length - 1) {
                              setActiveTab(tabs[currentIndex + 1]);
                            }
                          }
                        }}
                        disabled={saveInProgress}
                      >
                        {saveInProgress ? (
                          <SaveInProgress isVisible={true} />
                        ) : (
                          "Save & Next"
                        )}
                      </button>
                    </div>
                  ) : (
                    <>
                      <div
                        className="mb-3 mt-3 p-3 rounded"
                        style={{ backgroundColor: "rgba(255,255,255,0.05)" }}
                      >
                        <div className="form-check d-flex align-items-start mb-0">
                          <input
                            type="checkbox"
                            className="form-check-input"
                            id="authorization"
                            checked={isAuthorized}
                            onChange={(e) => setIsAuthorized(e.target.checked)}
                          />
                          <label
                            className="form-check-label ms-2"
                            htmlFor="authorization"
                          >
                            <small>
                              I authorize that I have authority to use the
                              entered Telephone Numbers, Calling Logo, and Call
                              Reasons
                            </small>
                          </label>
                        </div>
                      </div>
                      <div className="d-flex gap-2">
                        {canWithdraw && displayRecordId && (
                          <button
                            type="button"
                            className="btn btn-outline-warning"
                            onClick={async () => {
                              try {
                                await callApi("bcid/dir/action", HttpMethod.POST, {
                                  entity: "dir",
                                  entityId: displayRecordId,
                                  action: "withdraw",
                                });
                                onClose();
                              } catch (err: any) {
                                setWarningMessage(err?.response?.data?.error || "Failed to withdraw submission.");
                              }
                            }}
                          >
                            <i className="bi bi-arrow-counterclockwise me-1"></i>
                            Withdraw
                          </button>
                        )}
                        {canRevert && displayRecordId && (
                          <button
                            type="button"
                            className="btn btn-outline-danger"
                            onClick={async () => {
                              try {
                                await callApi("bcid/dir/action", HttpMethod.POST, {
                                  entity: "dir",
                                  entityId: displayRecordId,
                                  action: "revert",
                                });
                                onClose();
                              } catch (err: any) {
                                setWarningMessage(err?.response?.data?.error || "Failed to revert draft.");
                              }
                            }}
                          >
                            <i className="bi bi-trash me-1"></i>
                            Revert Draft
                          </button>
                        )}
                        <button
                          type="button"
                          className="btn btn-secondary"
                          onClick={() => handleSave(true)}
                          disabled={saveInProgress || !canEdit}
                        >
                          Save Draft
                        </button>
                        <button
                          type="button"
                          className="btn btn-primary"
                          onClick={() => {
                            if (!isAuthorized) {
                              setWarningMessage(
                                "Check the authorization box above to confirm you have authority to use the telephone numbers, calling logo, and call reasons before submitting for vetting.",
                              );
                              return;
                            }
                            setWarningMessage("");
                            void handleSave(false);
                          }}
                          disabled={saveInProgress || !canEdit}
                        >
                          {!saveInProgress ? (
                            "Submit for Vetting"
                          ) : (
                            <SaveInProgress isVisible={saveInProgress} />
                          )}
                        </button>
                      </div>
                    </>
                  )}
                </div>

                {activeTab === "identity" && (
                  <div className="p-2 d-flex flex-column">
                    <div
                      style={{
                        width: "350px",
                        height: "700px",
                        minHeight: "700px",
                        alignSelf: "center",
                        backgroundImage: "url('/images/incomingcall.jpg')",
                        backgroundSize: "cover",
                        backgroundPosition: "top center",
                        backgroundRepeat: "no-repeat",
                      }}
                    >
                      <div className="pt-5 pb-2 mb-2 text-white">&nbsp;</div>
                      <div className="pb-2 mb-2 text-white text-center">
                        {callingName}
                      </div>
                      <div style={{ height: "80px" }}>
                        {logoPreview && (
                          <img
                            src={logoPreview}
                            alt="Logo Preview"
                            style={{
                              width: "100%",
                              height: "100%",
                              objectFit: "contain",
                            }}
                          />
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      <ConfirmationModal
        isVisible={unsavedTabTarget !== null}
        title="Unsaved changes"
        message="You have unsaved changes on this display record. Click Save to keep your work, or Discard to undo your changes."
        confirmText="Save"
        cancelText="Discard"
        confirmVariant="primary"
        onCancel={() => {
          void (async () => {
            const target = unsavedTabTarget;
            setUnsavedTabTarget(null);
            await fetchDisplayRecordData();
            if (target) {
              setActiveTab(target);
            }
          })();
        }}
        onConfirm={() => {
          void (async () => {
            const target = unsavedTabTarget;
            setUnsavedTabTarget(null);
            const ok = await handleSave(true);
            if (ok && target) {
              setActiveTab(target);
            }
          })();
        }}
      />
    </>
  );
};

export default DisplayRecordModal;
